import * as React from 'react'
import InputRange from 'react-input-range';
import { toastr } from "react-redux-toastr";
import Button from 'reactstrap/lib/Button';
import Form from 'reactstrap/lib/Form';
import SidebarContainer from 'src/modules/Routing/Sidebar/SidebarContainer';
import Map from "src/share/components/Maps/Map/Map";
import ImageUploader from 'src/share/components/ImageUploader/ImageUploader';
import DateRange from 'src/share/components/DateRange/DateRange';
import { momentAddDays, timestampToMoment } from 'src/share/utils/utils';
import { Loader } from 'src/modules/Alert/Create/components/Loader';
import { LocationSuggester, IGeoLocation } from 'src/share/components/LocationSuggester/LocationSuggester';
import routes from 'src/modules/Routing/routes';
import { GeoSearchService } from 'src/share/services/geoSearch';
import useGeolocation, { initialGeolocation } from './hooks/useGeolocation';
import { Moment } from '../../../../node_modules/moment';
import "./GeoSearchManualForm.css";
import { isEmpty } from 'lodash';
import { RoutesModules } from 'src/modules/Routing/types';

interface IDateRangeState {
  startDate: Moment | null;
  endDate: Moment | null;
  focus: "startDate" | "endDate" | null;
}

const GeoSearchManualForm = (props): React.ReactElement => {
  const [isLoading, setIsLoading] = React.useState(false);
  const { geolocation, setGeolocation } = useGeolocation(props.history.location.state);
  const [image, setImage] = React.useState("");
  const [dateRange, setDateRange] = React.useState<IDateRangeState>({ startDate: null, endDate: null, focus: null });
  React.useEffect(() => {
    const newState = props.history.location.state;
    if (!isEmpty(newState)) {
      setTimeout(() => {
        setStateFromHistory(newState);
        props.history.replace(routes.GEOSEARCH_MANUAL_FORM, {})
      }, 250);
    }
  }, []);
  function setStateFromHistory(newState): void {
    setGeolocation(prevState => ({
      ...prevState,
      center: newState.center,
      radius: newState.radius
    }))
    setImage(newState.image);
    setDateRange((prevDateRange) => ({
      ...prevDateRange,
      startDate: timestampToMoment(newState.dateRange.startDate),
      endDate: timestampToMoment(newState.dateRange.endDate)
    }));
  }
  function formatMiles(value: number): string {
    return `${Math.round(value * 10) / 10} miles`;
  }
  function handleRangeChanged(value: number): void {
    setGeolocation(prevState => ({ ...prevState, radius: +value.toFixed(2) }));
  }
  function handleLocationSelected(newGeolocation: IGeoLocation): void {
    setGeolocation(prevState => ({ ...prevState, center: newGeolocation.center as any, isSelected: !!newGeolocation.countryCode }));
  }
  function handleDateRangeChanged(date): void {
    setDateRange(prevDateRange => ({ ...prevDateRange, ...date, endDate: momentAddDays(date.startDate, 1) }));
  }
  function handleReset(): void {
    setGeolocation(initialGeolocation);
    setImage("");
    setDateRange({ startDate: null, endDate: null, focus: null });
  }
  function handleDateFocusChanged({ focus }): void {
    if (focus === "startDate") {
      setDateRange((prevDateRange) => ({ ...prevDateRange, endDate: null, focus }));
    } else {
      setDateRange(prevDateRange => ({ ...prevDateRange, focus, endDate: prevDateRange.startDate ? momentAddDays(prevDateRange.startDate, 1) : null }))
    }
  }
  function handleCenterChanged(newCenter) {
    if (geolocation.address || props.history.location.state) {
      setGeolocation((prevState) => ({ ...prevState, center: newCenter }));
    }
  }
  async function sendRequest(): Promise<void> {
    if (geolocation.center && dateRange.startDate && dateRange.endDate) {
      try {
        setIsLoading(true);
        const searchId = await GeoSearchService.manualSearch({
          center: geolocation.center,
          dateRange: {
            gte: dateRange.startDate.toDate().getTime(),
            lte: dateRange.endDate.toDate().getTime()
          },
          image,
          radius: geolocation.radius
        })
        props.history.push(`${routes.GEOSEARCH_RESULT.replace(":searchId", searchId)}`);
      } catch (error) {
        toastr.error("Error", error.payload);
        setIsLoading(false);
      }
    }
  }
  function handleValidSubmit(e): void {
    e.preventDefault();
    if (!image) {
      toastr.warning("The image is required", "Please upload an image.");
      return;
    }
    if (!geolocation.isSelected) {
      toastr.warning("The geolocation is required", "Please select a location in the map.");
      return;
    }
    sendRequest();
  }
  return (
    <div className="geosearch-manual-search">
      <div className="white-background geosearch-manual-search__container">
        <h1>PDTR Manual Search</h1>
        <Form onSubmit={handleValidSubmit}>
          <div className="geosearch-manual-search__form">
            <div className="radius-range">
              <InputRange
                maxValue={7}
                minValue={1}
                allowSameValues={true}
                disabled={isLoading}
                value={geolocation.radius}
                formatLabel={formatMiles}
                onChange={handleRangeChanged}
                step={0.1}
              />
            </div>
            <div className="geosearch-map">
              <LocationSuggester
                address={geolocation.address}
                onSelect={handleLocationSelected}
                onChange={(newAddress): void => setGeolocation((prevState) => ({ ...prevState, address: newAddress }))}
                required={true}
                disabled={isLoading}
              />
              <Map
                radius={geolocation.radius}
                center={geolocation.center}
                onMapChange={handleCenterChanged}
                isLocationSelected={geolocation.isSelected}
              />
            </div>
            <div className="input-group">
              <div className="date-input">
                <h5>Date Filter:</h5>
                <DateRange
                  onDatesChange={handleDateRangeChanged}
                  onFocusChange={handleDateFocusChanged}
                  focusedInput={dateRange.focus}
                  numberOfMonths={1}
                  disabled={isLoading ? true : "endDate"}
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  required={true}
                />
              </div>
              <div className="image-input">
                <ImageUploader
                  thumbnail={image}
                  isLoading={false}
                  onDropAccepted={(img): void => setImage(img)}
                  onImageRemoved={(): void => setImage("")}
                />
              </div>
            </div>
            <div className="actions">
              {isLoading ?
                <Loader loading={isLoading} /> :
                <>
                  <Button type="submit" disabled={isLoading} className="btn btn-outline btn-green-fill float-right">
                    Submit
                  </Button>
                  <Button disabled={isLoading} onClick={handleReset} className="btn btn-outline btn-grey-fill float-right">
                    Clear all
                  </Button>
                </>
              }
            </div>
          </div>
        </Form>
      </div>
    </div>
  )
}

export default SidebarContainer(RoutesModules.GEOSEARCH)(GeoSearchManualForm);



// WEBPACK FOOTER //
// ./src/modules/GeoSearch/ManualSearch/GeoSearchManualForm.tsx